import React, { useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { X, LogIn, ShieldCheck, Sparkles, Mail, Lock, User as UserIcon, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  fr: {
    title: "Rejoignez GEM PRICE",
    subtitle: "Créez votre compte en 30 secondes — accès à votre historique, votre abonnement et vos préférences.",
    tabGoogle: "Google",
    tabSignin: "Se connecter",
    tabRegister: "Créer un compte",
    email: "Adresse email",
    password: "Mot de passe (min. 8 caractères)",
    name: "Prénom (facultatif)",
    signinCta: "Se connecter",
    registerCta: "Créer mon compte",
    googleCta: "Continuer avec Google",
    b1: "Aucun mot de passe à retenir",
    b2: "Vos données sont chiffrées et jamais revendues",
    b3: "Récupération automatique de votre abonnement",
    optional: "Vous pouvez continuer à naviguer sans compte — la création est nécessaire uniquement au moment de l'abonnement.",
    close: "Continuer sans compte",
    or: "OU",
    genericError: "Une erreur est survenue. Veuillez réessayer.",
  },
  en: {
    title: "Join GEM PRICE",
    subtitle: "Create your account in 30 seconds — access your history, subscription and preferences.",
    tabGoogle: "Google",
    tabSignin: "Sign in",
    tabRegister: "Create account",
    email: "Email address",
    password: "Password (min. 8 characters)",
    name: "First name (optional)",
    signinCta: "Sign in",
    registerCta: "Create my account",
    googleCta: "Continue with Google",
    b1: "No password to remember",
    b2: "Your data is encrypted and never resold",
    b3: "Automatic recovery of existing subscription",
    optional: "You can keep browsing without an account — signing in is only required when subscribing.",
    close: "Continue without account",
    or: "OR",
    genericError: "An error occurred. Please try again.",
  },
};

const GoogleGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.9 0 6.6 1.7 8.1 3.1l5.9-5.8C34.6 3.2 29.8 1 24 1 14.9 1 7.2 6.4 3.5 14l6.9 5.4C12.2 13.1 17.6 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.5 2.9-2.2 5.4-4.6 7l7.1 5.5c4.2-3.9 6.6-9.6 6.6-17z"/>
    <path fill="#FBBC05" d="M10.4 28.6c-.5-1.4-.8-2.9-.8-4.6s.3-3.2.8-4.6l-6.9-5.4C1.9 17.2 1 20.5 1 24s.9 6.8 2.5 10l6.9-5.4z"/>
    <path fill="#34A853" d="M24 47c6.5 0 12-2.1 16-5.9l-7.1-5.5c-2 1.4-4.6 2.3-8.9 2.3-6.4 0-11.8-3.6-13.6-9.9l-6.9 5.4C7.2 41.6 14.9 47 24 47z"/>
  </svg>
);

function formatApiErrorDetail(detail, fallback) {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ") || fallback;
  if (detail && typeof detail.msg === "string") return detail.msg;
  return fallback;
}

export const AuthModal = ({ open, onClose, reason }) => {
  const { login: googleLogin, refresh } = useAuth();
  const { lang } = useLang();
  const t = T[lang];
  const [tab, setTab] = useState("signin"); // signin | register
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Lock body scroll while modal is open (prevents underlying page from scrolling)
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    const url = tab === "register" ? "/auth/register" : "/auth/login";
    try {
      await axios.post(`${API}${url}`, form, { withCredentials: true });
      await refresh();
      setBusy(false);
      onClose?.();
    } catch (err) {
      setBusy(false);
      setError(formatApiErrorDetail(err?.response?.data?.detail, t.genericError));
    }
  };

  const setF = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const modal = (
    <div
      data-testid="auth-modal"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="min-h-full flex items-start md:items-center justify-center p-4 py-8">
        <div className="relative w-full max-w-md bg-[#0A0F1F] border border-[#C1A661]/25 rounded-sm p-6 md:p-7 shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
        <button
          onClick={onClose}
          data-testid="auth-modal-close"
          aria-label="Close"
          className="absolute top-3 right-3 text-[#8A8F99] hover:text-[#E2CFA4] transition-colors p-2 rounded-full hover:bg-[#C1A661]/10"
        >
          <X size={20} />
        </button>

        <div className="flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-full border border-[#C1A661]/30 flex items-center justify-center mb-3">
            <LogIn size={20} strokeWidth={1.3} className="text-[#C1A661]" />
          </div>
          <h2 className="font-serif text-xl md:text-2xl text-[#E2CFA4] tracking-wide">{t.title}</h2>
          {reason && <p className="mt-1 text-[10px] tracking-[0.2em] uppercase text-[#C1A661]">{reason}</p>}
          <p className="mt-2 text-[12px] text-[#A1A7B3] font-light leading-relaxed">{t.subtitle}</p>
        </div>

        {/* Tabs */}
        <div className="mt-5 grid grid-cols-2 gap-1 rounded-sm bg-[#050811] border border-[#C1A661]/15 p-1">
          <button
            data-testid="auth-tab-signin"
            onClick={() => { setTab("signin"); setError(""); }}
            className={`py-1.5 text-[10px] tracking-[0.2em] uppercase rounded-sm ${tab === "signin" ? "bg-[#C1A661]/15 text-[#E2CFA4]" : "text-[#A1A7B3]"}`}
          >{t.tabSignin}</button>
          <button
            data-testid="auth-tab-register"
            onClick={() => { setTab("register"); setError(""); }}
            className={`py-1.5 text-[10px] tracking-[0.2em] uppercase rounded-sm ${tab === "register" ? "bg-[#C1A661]/15 text-[#E2CFA4]" : "text-[#A1A7B3]"}`}
          >{t.tabRegister}</button>
        </div>

        <form onSubmit={submit} className="mt-4 space-y-3" data-testid="auth-email-form">
          {tab === "register" && (
            <div className="flex items-center gap-2 border border-[#C1A661]/25 rounded-sm px-3 py-2 bg-[#050811]">
              <UserIcon size={14} className="text-[#C1A661]" />
              <input
                data-testid="auth-input-name"
                type="text" placeholder={t.name}
                value={form.name} onChange={setF("name")}
                className="flex-1 bg-transparent outline-none text-sm text-[#E2CFA4] placeholder:text-[#8A8F99]"
              />
            </div>
          )}
          <div className="flex items-center gap-2 border border-[#C1A661]/25 rounded-sm px-3 py-2 bg-[#050811]">
            <Mail size={14} className="text-[#C1A661]" />
            <input
              data-testid="auth-input-email"
              type="email" required placeholder={t.email} autoComplete="email"
              value={form.email} onChange={setF("email")}
              className="flex-1 bg-transparent outline-none text-sm text-[#E2CFA4] placeholder:text-[#8A8F99]"
            />
          </div>
          <div className="flex items-center gap-2 border border-[#C1A661]/25 rounded-sm px-3 py-2 bg-[#050811]">
            <Lock size={14} className="text-[#C1A661]" />
            <input
              data-testid="auth-input-password"
              type="password" required minLength={8} placeholder={t.password}
              autoComplete={tab === "register" ? "new-password" : "current-password"}
              value={form.password} onChange={setF("password")}
              className="flex-1 bg-transparent outline-none text-sm text-[#E2CFA4] placeholder:text-[#8A8F99]"
            />
          </div>
          {error && (
            <div data-testid="auth-error" className="text-[11px] text-[#F4A3A3] bg-[#F4A3A3]/10 border border-[#F4A3A3]/25 rounded-sm px-3 py-2">
              {error}
            </div>
          )}
          <button
            type="submit" disabled={busy}
            data-testid="auth-submit-btn"
            className="w-full flex items-center justify-center gap-2 rounded-sm bg-[#C1A661] hover:bg-[#E2CFA4] disabled:opacity-60 text-[#050B1E] font-medium py-2.5 px-4 text-sm tracking-wide transition-colors"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {tab === "register" ? t.registerCta : t.signinCta}
          </button>
        </form>

        <div className="my-4 flex items-center gap-3 text-[10px] tracking-[0.3em] text-[#8A8F99]">
          <span className="flex-1 h-px bg-[#C1A661]/15" />
          {t.or}
          <span className="flex-1 h-px bg-[#C1A661]/15" />
        </div>

        <button
          onClick={googleLogin}
          data-testid="auth-google-btn"
          className="w-full flex items-center justify-center gap-3 rounded-sm border border-[#C1A661]/30 hover:border-[#C1A661] hover:bg-[#C1A661]/5 text-[#E2CFA4] py-2.5 px-4 text-sm transition-colors"
        >
          <GoogleGlyph />
          {t.googleCta}
        </button>

        <p className="mt-4 text-[10px] italic text-[#8A8F99] leading-relaxed text-center">{t.optional}</p>
          <div className="mt-2 text-center">
          <button
            onClick={onClose}
            data-testid="auth-modal-skip"
            className="text-[10px] tracking-[0.25em] uppercase text-[#C1A661] hover:text-[#E2CFA4] underline-offset-4 hover:underline"
          >
            {t.close}
          </button>
        </div>
        </div>
      </div>
    </div>
  );
  return createPortal(modal, document.body);
};

export default AuthModal;
